import { FetchError, type ReferralFees, ReferralFeesSchema } from "@decibeltrade/sdk";
import { type FetchQueryOptions, useQuery } from "@tanstack/react-query";

import type { GetAuthHeaders } from "@/utils/signature-auth";

import { useWriteDexSdk } from "../write-dex-sdk-context";
import { fetchAffiliateRead } from "./affiliate-read";

export const REFERRAL_FEES_QUERY_KEY = "referralFees";

export interface ReferralFeesQueryArgs {
  referrerAccount: string | undefined;
  /** Trailing UTC days to cover. Server defaults to 30, clamps to 90. */
  days?: number;
  /**
   * Supplied by the hook, not the caller: `X-Subaccount-Address` is what lets the backend notice a
   * stale session cookie after a wallet switch, and without it wallet A's data can land in wallet
   * B's cache. See `affiliate-read.ts`.
   */
  getAuthHeaders?: GetAuthHeaders;
  subaccountAddress?: string;
}

export function referralFeesQueryKey({ referrerAccount, days }: ReferralFeesQueryArgs) {
  return [REFERRAL_FEES_QUERY_KEY, referrerAccount, days] as const;
}

/** A referrer whose network has generated nothing yet, which is not a failure. */
function emptyFees(days: number | undefined): ReferralFees {
  return {
    net_fees_usd: 0,
    builder_payouts_usd: 0,
    commission_basis_usd: 0,
    days: days ?? 30,
  };
}

export function referralFeesQuery({
  referrerAccount,
  days,
  getAuthHeaders,
  subaccountAddress,
}: ReferralFeesQueryArgs) {
  return {
    queryKey: referralFeesQueryKey({ referrerAccount, days }),
    queryFn: async () => {
      if (!referrerAccount) return emptyFees(days);

      try {
        return await fetchAffiliateRead({
          endpoint: "fees",
          schema: ReferralFeesSchema,
          params: { days },
          getAuthHeaders,
          subaccountAddress,
        });
      } catch (error) {
        if (error instanceof FetchError && error.status === 404) return emptyFees(days);
        throw error;
      }
    },
    retry(failureCount: number, error: Error) {
      if (error instanceof FetchError && error.status === 404) return false;
      return failureCount < 3;
    },
  } satisfies FetchQueryOptions<ReferralFees>;
}

export function useReferralFeesQuery(args: ReferralFeesQueryArgs) {
  const { getAuthHeaders, subaccountAddress } = useWriteDexSdk();
  return useQuery({
    ...referralFeesQuery({ ...args, getAuthHeaders, subaccountAddress }),
    // Waits for the session, like every consumer of signatureAuthMiddleware: an
    // unauthenticated read is a guaranteed 401 whose SIWA retry cannot run, and the
    // query key carries no auth, so nothing would refetch when the session hydrates.
    enabled: !!args.referrerAccount && !!getAuthHeaders,
  });
}
